import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PhoneInput, { toInternationalPhoneDigits, isValidPhone } from '@/components/ui/phone-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FieldError } from '@/components/ui/field-error';

interface NewTenantResponse {
  tenant: { id: string };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

export function TenantFormDialog({ open, onOpenChange, onCreated }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    fullName: '',
    displayName: '',
    email: '',
    status: 'onboarding' as 'onboarding' | 'active',
    ruc: '',
    razonSocial: '',
    regimen: 'iva_irp' as 'iva_irp' | 'ire_simple',
  });
  const [phoneCountry, setPhoneCountry] = useState('PY');
  const [phoneNational, setPhoneNational] = useState('');
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  const phoneValid = isValidPhone(phoneCountry, phoneNational);

  const mutation = useMutation({
    mutationFn: () =>
      api<NewTenantResponse>('/api/admin/tenants', {
        method: 'POST',
        body: JSON.stringify({
          whatsappNumber: toInternationalPhoneDigits(phoneCountry, phoneNational),
          fullName: form.fullName.trim(),
          displayName: form.displayName.trim() || form.fullName.split(' ')[0],
          email: form.email.trim() || undefined,
          status: form.status,
          initialRuc: {
            ruc: form.ruc.trim(),
            razonSocial: form.razonSocial.trim() || form.fullName.toUpperCase(),
            regimen: form.regimen,
          },
        }),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      toast.success(`Tenant creado: ${form.fullName}`);
      onCreated?.(data.tenant.id);
      onOpenChange(false);
      setForm({
        fullName: '',
        displayName: '',
        email: '',
        status: 'onboarding',
        ruc: '',
        razonSocial: '',
        regimen: 'iva_irp',
      });
      setPhoneCountry('PY');
      setPhoneNational('');
      setIssues({});
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.status === 409 && err.body.error === 'whatsapp_already_exists') {
          setIssues({ whatsappNumber: ['Ya existe un contacto con ese WhatsApp'] });
        } else if (err.body.issues) {
          setIssues(err.body.issues);
        } else {
          setIssues({ _form: [err.body.error] });
        }
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Nuevo contacto</DialogTitle>
          <DialogDescription>Datos del contacto y su RUC principal</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="whatsappNumber" required>
                WhatsApp
              </Label>
              <PhoneInput
                id="whatsappNumber"
                showLabel={false}
                countryCode={phoneCountry}
                phoneNumber={phoneNational}
                onCountryCodeChange={setPhoneCountry}
                onPhoneNumberChange={setPhoneNational}
                placeholder="0991 123-456"
                required
              />
              {phoneNational.trim() !== '' && !phoneValid && (
                <FieldError error="Número inválido para el país elegido." />
              )}
              <FieldError error={issues.whatsappNumber} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email (opcional)</Label>
              <Input
                id="email"
                type="email"
                placeholder="cliente@ejemplo.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <FieldError error={issues.email} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="fullName" required>
              Nombre completo
            </Label>
            <Input
              id="fullName"
              placeholder="María González"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              required
            />
            <FieldError error={issues.fullName} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="displayName">Nombre corto (chat)</Label>
              <Input
                id="displayName"
                placeholder="María"
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Estado inicial</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v as 'onboarding' | 'active' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="active">Activo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">RUC principal</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ruc" required>
                  RUC
                </Label>
                <Input
                  id="ruc"
                  placeholder="4567890-1"
                  value={form.ruc}
                  onChange={(e) => setForm({ ...form, ruc: e.target.value })}
                  required
                />
                <FieldError error={issues['initialRuc.ruc']} />
              </div>
              <div className="space-y-1.5">
                <Label required>Régimen</Label>
                <Select
                  value={form.regimen}
                  onValueChange={(v) => setForm({ ...form, regimen: v as 'iva_irp' | 'ire_simple' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="iva_irp">IVA + IRP (personas físicas)</SelectItem>
                    <SelectItem value="ire_simple">IRE Simple</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5 mt-3">
              <Label htmlFor="razonSocial">Razón social (en SET)</Label>
              <Input
                id="razonSocial"
                placeholder="GONZALEZ MARIA"
                value={form.razonSocial}
                onChange={(e) => setForm({ ...form, razonSocial: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Si la dejás vacía, se usa el nombre completo en mayúsculas.
              </p>
            </div>
          </div>

          {issues._form && <p className="text-sm text-destructive">Error: {issues._form[0]}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !phoneValid}>
              {mutation.isPending ? 'Creando…' : 'Crear contacto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
